import React from "react";
import { CHECKOUT_WIZARD_STEPS, CheckoutWizardStepId } from "./checkoutWizard.constants";
import { useCheckoutContext } from "../CheckoutContext";
import { cn } from "@/lib/utils";

interface CheckoutWizardRailProps {
  currentStep: CheckoutWizardStepId;
  maxReachableStep: CheckoutWizardStepId;
  onGoToStep: (step: CheckoutWizardStepId) => void;
}

export const CheckoutWizardRail: React.FC<CheckoutWizardRailProps> = ({
  currentStep,
  maxReachableStep,
  onGoToStep,
}) => {
  const { getFinalPrice, formatCurrency } = useCheckoutContext();

  return (
    <nav
      className="hidden lg:sticky lg:top-[4.5rem] lg:self-start lg:flex lg:h-[calc(100svh-4.5rem)] w-[17rem] shrink-0 flex-col border-r border bg-background px-6 py-8"
      aria-label="Etapas do checkout"
    >
      <div className="shrink-0 space-y-0.5">
        <p className="text-lg font-semibold text-foreground">OfCourse</p>
        <p className="text-xs text-text-secondary">Finalizar compra</p>
      </div>

      <div
        className="mt-6 shrink-0 rounded-lg border border-success bg-success-muted px-4 py-3.5"
        aria-live="polite"
        aria-atomic="true"
      >
        <p className="text-xs font-medium text-success-subtle">Total do pedido</p>
        <p className="mt-1 text-2xl font-semibold tracking-tight text-success-subtle">
          {formatCurrency(getFinalPrice())}
        </p>
      </div>

      <ol className="mt-8">
        {CHECKOUT_WIZARD_STEPS.map((step, index) => {
          const isActive = currentStep === step.id;
          const isDone = step.id < currentStep;
          const canNavigate = step.id <= maxReachableStep;
          const isLast = index === CHECKOUT_WIZARD_STEPS.length - 1;

          return (
            <li key={step.id} className={cn("relative", !isLast && "pb-8")}>
              {!isLast ? (
                <span
                  aria-hidden
                  className={cn(
                    "pointer-events-none absolute left-[1.125rem] top-9 bottom-0 w-0.5 -translate-x-1/2",
                    isDone ? "bg-success" : "bg-border"
                  )}
                />
              ) : null}
              <button
                type="button"
                onClick={() => canNavigate && onGoToStep(step.id)}
                disabled={!canNavigate}
                aria-current={isActive ? "step" : undefined}
                className={cn(
                  "relative flex min-h-11 w-full items-start gap-3 text-left transition-colors",
                  "!rounded-none !border-transparent !bg-transparent !p-0 !shadow-none",
                  "hover:!border-transparent hover:!bg-transparent",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  canNavigate ? "cursor-pointer hover:opacity-80" : "cursor-default",
                  !canNavigate && "opacity-60"
                )}
              >
                <span
                  className={cn(
                    "relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors",
                    isActive && "border-success bg-success text-success-foreground",
                    isDone && !isActive && "border-success bg-success-muted text-success-subtle",
                    !isActive && !isDone && "border-border bg-background text-text-tertiary"
                  )}
                >
                  {step.id + 1}
                </span>
                <div className="min-w-0 pt-2">
                  <p
                    className={cn(
                      "text-sm font-semibold",
                      isActive ? "text-foreground" : isDone ? "text-foreground" : "text-text-tertiary"
                    )}
                  >
                    {step.label}
                  </p>
                  <p className="text-xs text-text-tertiary">{step.hint}</p>
                </div>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
